import React, { useState, useEffect } from 'react';
import { Stethoscope, Plus, Edit, Trash2, Search, DollarSign, Clock, CheckCircle, XCircle, Star, Building } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function TreatmentManagement() {
  const [treatments, setTreatments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedTreatment, setSelectedTreatment] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    description: '',
    min_price: '',
    max_price: '',
    currency: 'USD',
    duration_days: '',
    recovery_time: '',
    success_rate: '',
    hospitals_offering: '',
    procedures_included: '',
    pre_requirements: '',
    post_care: '',
    is_active: true
  });
  const [filters, setFilters] = useState({
    search: '',
    category: 'all',
    status: 'all'
  });

  const categories = [
    'Cardiac Care', 'Orthopedics', 'Oncology', 'Neurology', 'Transplant',
    'Cosmetic Surgery', 'Dental', 'Eye Care', 'Fertility', 'Bariatric',
    'Spine Surgery', 'Joint Replacement', 'Cancer Treatment', 'Kidney Care',
    'Liver Care', 'Wellness & Ayurveda', 'Diagnostics'
  ];

  useEffect(() => {
    fetchTreatments();
  }, []);

  const fetchTreatments = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/treatments-management`, {
        headers: { }
      });

      if (response.ok) {
        const data = await response.json();
        setTreatments(data.treatments || []);
      }
    } catch (err) {
      toast.error('Failed to fetch treatments');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/treatments-management`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Treatment created successfully');
        setShowCreateDialog(false);
        resetForm();
        fetchTreatments();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create treatment');
      }
    } catch (err) {
      toast.error('Failed to create treatment');
    }
  };

  const handleUpdate = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/treatments-management/${selectedTreatment._id}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Treatment updated successfully');
        setShowEditDialog(false);
        resetForm();
        fetchTreatments();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update treatment');
      }
    } catch (err) {
      toast.error('Failed to update treatment');
    }
  };

  const handleDelete = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/treatments-management/${selectedTreatment._id}`, {
        method: 'DELETE',
        headers: { }
      });

      if (response.ok) {
        toast.success('Treatment deleted successfully');
        setShowDeleteDialog(false);
        setSelectedTreatment(null);
        fetchTreatments();
      } else {
        toast.error('Failed to delete treatment');
      }
    } catch (err) {
      toast.error('Failed to delete treatment');
    }
  };

  const handleToggleStatus = async (treatmentId, currentStatus) => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/treatments-management/${treatmentId}/status`, {
        method: 'PATCH',
        headers: { 
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_active: !currentStatus })
      });

      if (response.ok) {
        toast.success(`Treatment ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
        fetchTreatments();
      }
    } catch (err) {
      toast.error('Failed to update treatment status');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      category: '',
      description: '',
      min_price: '',
      max_price: '',
      currency: 'USD',
      duration_days: '',
      recovery_time: '',
      success_rate: '',
      hospitals_offering: '',
      procedures_included: '',
      pre_requirements: '',
      post_care: '',
      is_active: true
    });
    setSelectedTreatment(null);
  };

  const openEditDialog = (treatment) => {
    setSelectedTreatment(treatment);
    setFormData({
      name: treatment.name || '',
      category: treatment.category || '',
      description: treatment.description || '',
      min_price: treatment.min_price || '',
      max_price: treatment.max_price || '',
      currency: treatment.currency || 'USD',
      duration_days: treatment.duration_days || '',
      recovery_time: treatment.recovery_time || '',
      success_rate: treatment.success_rate || '',
      hospitals_offering: treatment.hospitals_offering || '',
      procedures_included: treatment.procedures_included || '',
      pre_requirements: treatment.pre_requirements || '',
      post_care: treatment.post_care || '',
      is_active: treatment.is_active !== false
    });
    setShowEditDialog(true);
  };

  const filteredTreatments = treatments.filter(treatment => {
    const matchesSearch = filters.search === '' || 
      treatment.name?.toLowerCase().includes(filters.search.toLowerCase()) ||
      treatment.category?.toLowerCase().includes(filters.search.toLowerCase());
    const matchesCategory = filters.category === 'all' || treatment.category === filters.category;
    const matchesStatus = filters.status === 'all' || 
      (filters.status === 'active' && treatment.is_active !== false) ||
      (filters.status === 'inactive' && treatment.is_active === false);
    return matchesSearch && matchesCategory && matchesStatus;
  });

  const stats = {
    total: treatments.length,
    active: treatments.filter(t => t.is_active !== false).length,
    inactive: treatments.filter(t => t.is_active === false).length,
    categories: [...new Set(treatments.map(t => t.category))].length
  };

  const formatPrice = (min, max, currency = 'USD') => {
    const symbol = currency === 'USD' ? '$' : currency === 'INR' ? '₹' : currency;
    if (min && max) return `${symbol}${min.toLocaleString()} - ${symbol}${max.toLocaleString()}`;
    if (min) return `From ${symbol}${min.toLocaleString()}`;
    if (max) return `Up to ${symbol}${max.toLocaleString()}`;
    return 'Price on request';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading treatments...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="treatment-management">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Treatment Management</h1>
          <p className="text-gray-600 mt-1">Manage medical treatments and procedures</p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} className="bg-teal-600 hover:bg-teal-700" data-testid="add-treatment-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Treatment
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Treatments</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
              <Stethoscope className="w-8 h-8 text-teal-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active</p>
                <p className="text-2xl font-bold text-green-600">{stats.active}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Inactive</p>
                <p className="text-2xl font-bold text-gray-600">{stats.inactive}</p>
              </div>
              <XCircle className="w-8 h-8 text-gray-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Categories</p>
                <p className="text-2xl font-bold text-purple-600">{stats.categories}</p>
              </div>
              <Star className="w-8 h-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search treatments..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="pl-10"
                  data-testid="search-treatments"
                />
              </div>
            </div>
            <Select value={filters.category} onValueChange={(v) => setFilters({ ...filters, category: v })}>
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
              <SelectTrigger className="w-full md:w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Treatments Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="treatments-grid">
        {filteredTreatments.length === 0 ? (
          <div className="col-span-full text-center py-12 text-gray-500">
            No treatments found
          </div>
        ) : (
          filteredTreatments.map((treatment) => (
            <Card key={treatment._id} className={`hover:shadow-lg transition-shadow ${treatment.is_active === false ? 'opacity-60' : ''}`} data-testid={`treatment-card-${treatment._id}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg text-gray-900">{treatment.name}</h3>
                    <Badge className="mt-1 bg-teal-100 text-teal-800">{treatment.category || 'General'}</Badge>
                  </div>
                  <Badge className={treatment.is_active !== false ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                    {treatment.is_active !== false ? 'Active' : 'Inactive'}
                  </Badge>
                </div>

                <p className="text-sm text-gray-600 mb-4 line-clamp-2">{treatment.description || 'No description available'}</p>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm">
                    <DollarSign className="w-4 h-4 text-gray-400" />
                    <span className="font-medium">{formatPrice(treatment.min_price, treatment.max_price, treatment.currency)}</span>
                  </div>
                  {treatment.duration_days && (
                    <div className="flex items-center gap-2 text-sm">
                      <Clock className="w-4 h-4 text-gray-400" />
                      <span>{treatment.duration_days} days treatment</span>
                    </div>
                  )}
                  {treatment.success_rate && (
                    <div className="flex items-center gap-2 text-sm">
                      <Star className="w-4 h-4 text-yellow-500" />
                      <span>{treatment.success_rate}% success rate</span>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2 pt-4 border-t">
                  <Button variant="outline" size="sm" className="flex-1" onClick={() => openEditDialog(treatment)} data-testid={`edit-treatment-${treatment._id}`}>
                    <Edit className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleToggleStatus(treatment._id, treatment.is_active !== false)}
                    className={treatment.is_active !== false ? 'text-gray-600' : 'text-green-600'}
                  >
                    {treatment.is_active !== false ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => { setSelectedTreatment(treatment); setShowDeleteDialog(true); }}
                    className="text-red-600"
                    data-testid={`delete-treatment-${treatment._id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Create Treatment Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-treatment-dialog">
          <DialogHeader>
            <DialogTitle>Add New Treatment</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-1">Treatment Name *</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Heart Bypass Surgery"
                data-testid="input-treatment-name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Category *</label>
              <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                <SelectTrigger data-testid="select-category">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Currency</label>
              <Select value={formData.currency} onValueChange={(v) => setFormData({ ...formData, currency: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select currency" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">USD ($)</SelectItem>
                  <SelectItem value="INR">INR (₹)</SelectItem>
                  <SelectItem value="EUR">EUR (€)</SelectItem>
                  <SelectItem value="GBP">GBP (£)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Min Price</label>
              <Input
                type="number"
                value={formData.min_price}
                onChange={(e) => setFormData({ ...formData, min_price: e.target.value })}
                placeholder="5000"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Max Price</label>
              <Input
                type="number"
                value={formData.max_price}
                onChange={(e) => setFormData({ ...formData, max_price: e.target.value })}
                placeholder="15000"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Duration (Days)</label>
              <Input
                type="number"
                value={formData.duration_days}
                onChange={(e) => setFormData({ ...formData, duration_days: e.target.value })}
                placeholder="7"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Recovery Time</label>
              <Input
                value={formData.recovery_time}
                onChange={(e) => setFormData({ ...formData, recovery_time: e.target.value })}
                placeholder="2-3 weeks"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Success Rate (%)</label>
              <Input
                type="number"
                value={formData.success_rate}
                onChange={(e) => setFormData({ ...formData, success_rate: e.target.value })}
                placeholder="95"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Hospitals Offering</label>
              <Input
                value={formData.hospitals_offering}
                onChange={(e) => setFormData({ ...formData, hospitals_offering: e.target.value })}
                placeholder="Apollo, Fortis, Max"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-1">Description</label>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                rows="3"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Detailed description of the treatment..."
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-1">Procedures Included</label>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                rows="2"
                value={formData.procedures_included}
                onChange={(e) => setFormData({ ...formData, procedures_included: e.target.value })}
                placeholder="List of procedures included..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Pre-Requirements</label>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                rows="2"
                value={formData.pre_requirements}
                onChange={(e) => setFormData({ ...formData, pre_requirements: e.target.value })}
                placeholder="Pre-operative requirements..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Post Care</label>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                rows="2"
                value={formData.post_care}
                onChange={(e) => setFormData({ ...formData, post_care: e.target.value })}
                placeholder="Post-operative care instructions..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreateDialog(false); resetForm(); }}>Cancel</Button>
            <Button onClick={handleCreate} className="bg-teal-600 hover:bg-teal-700" data-testid="submit-create-treatment">Create Treatment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Treatment Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-treatment-dialog">
          <DialogHeader>
            <DialogTitle>Edit Treatment</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-1">Treatment Name *</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Category *</label>
              <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Currency</label>
              <Select value={formData.currency} onValueChange={(v) => setFormData({ ...formData, currency: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select currency" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">USD ($)</SelectItem>
                  <SelectItem value="INR">INR (₹)</SelectItem>
                  <SelectItem value="EUR">EUR (€)</SelectItem>
                  <SelectItem value="GBP">GBP (£)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Min Price</label>
              <Input
                type="number"
                value={formData.min_price}
                onChange={(e) => setFormData({ ...formData, min_price: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Max Price</label>
              <Input
                type="number"
                value={formData.max_price}
                onChange={(e) => setFormData({ ...formData, max_price: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Duration (Days)</label>
              <Input
                type="number"
                value={formData.duration_days}
                onChange={(e) => setFormData({ ...formData, duration_days: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Recovery Time</label>
              <Input
                value={formData.recovery_time}
                onChange={(e) => setFormData({ ...formData, recovery_time: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Success Rate (%)</label>
              <Input
                type="number"
                value={formData.success_rate}
                onChange={(e) => setFormData({ ...formData, success_rate: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Hospitals Offering</label>
              <Input
                value={formData.hospitals_offering}
                onChange={(e) => setFormData({ ...formData, hospitals_offering: e.target.value })}
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-1">Description</label>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                rows="3"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-1">Procedures Included</label>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                rows="2"
                value={formData.procedures_included}
                onChange={(e) => setFormData({ ...formData, procedures_included: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Pre-Requirements</label>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                rows="2"
                value={formData.pre_requirements}
                onChange={(e) => setFormData({ ...formData, pre_requirements: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Post Care</label>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                rows="2"
                value={formData.post_care}
                onChange={(e) => setFormData({ ...formData, post_care: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowEditDialog(false); resetForm(); }}>Cancel</Button>
            <Button onClick={handleUpdate} className="bg-teal-600 hover:bg-teal-700" data-testid="submit-edit-treatment">Update Treatment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent data-testid="delete-treatment-dialog">
          <DialogHeader>
            <DialogTitle>Delete Treatment</DialogTitle>
          </DialogHeader>
          <p className="py-4">Are you sure you want to delete <strong>{selectedTreatment?.name}</strong>? This action cannot be undone.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
            <Button onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-treatment">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
